/**
 * Breadcrumb Schema Generator
 * Generates BreadcrumbList JSON-LD structured data for hierarchical pages
 *
 * Requirements:
 * - Works with all locales (en, de, pt-BR, ms-MY, es)
 * - Uses canonical URLs
 * - Localized breadcrumb labels
 * - Follows Schema.org specification
 */

import { urlMappings } from './hreflang';
import { getCanonicalUrl } from './canonicalUrl';

export type Locale = 'en' | 'de' | 'pt-BR' | 'ms-MY' | 'es' | 'fr' | 'ja' | 'id';

interface BreadcrumbItem {
  '@type': 'ListItem';
  position: number;
  name: string;
  item: string;
}

export interface BreadcrumbListSchema {
  '@context': 'https://schema.org';
  '@type': 'BreadcrumbList';
  itemListElement: BreadcrumbItem[];
}

/**
 * Localized labels for common breadcrumb items
 */
const BREADCRUMB_LABELS: Record<string, Record<Locale, string>> = {
  'home': {
    'en': 'Home',
    'de': 'Startseite',
    'pt-BR': 'Início',
    'ms-MY': 'Laman Utama',
    'es': 'Inicio',
    'fr': 'Accueil',
    'ja': 'ホーム',
    'id': 'Beranda',
  },
  'blog': {
    'en': 'Blog',
    'de': 'Blog',
    'pt-BR': 'Blog',
    'ms-MY': 'Blog',
    'es': 'Blog',
    'fr': 'Blog',
    'ja': 'ブログ',
    'id': 'Blog',
  },
  'knowledge': {
    'en': 'Knowledge',
    'de': 'Wissen',
    'pt-BR': 'Conhecimento',
    'ms-MY': 'Pengetahuan',
    'es': 'Conocimiento',
    'fr': 'Connaissances',
    'ja': 'ナレッジ',
    'id': 'Pengetahuan',
  },
  'about': {
    'en': 'About',
    'de': 'Über uns',
    'pt-BR': 'Sobre',
    'ms-MY': 'Tentang Kami',
    'es': 'Acerca de',
    'fr': 'À propos',
    'ja': '概要',
    'id': 'Tentang',
  },
  'detect': {
    'en': 'Detect',
    'de': 'Erkennung',
    'pt-BR': 'Detecção',
    'ms-MY': 'Pengesanan',
    'es': 'Detectar',
    'fr': 'Détecter',
    'ja': '検出',
    'id': 'Deteksi',
  },
  'document': {
    'en': 'Document',
    'de': 'Dokument',
    'pt-BR': 'Documento',
    'ms-MY': 'Dokumen',
    'es': 'Documento',
    'fr': 'Document',
    'ja': 'ドキュメント',
    'id': 'Dokumen',
  },
  'category': {
    'en': 'Category',
    'de': 'Kategorie',
    'pt-BR': 'Categoria',
    'ms-MY': 'Kategori',
    'es': 'Categoría',
    'fr': 'Catégorie',
    'ja': 'カテゴリ',
    'id': 'Kategori',
  },
  'tag': {
    'en': 'Tag',
    'de': 'Tag',
    'pt-BR': 'Tag',
    'ms-MY': 'Tag',
    'es': 'Etiqueta',
    'fr': 'Tag',
    'ja': 'タグ',
    'id': 'Tag',
  },
  'pricing': {
    'en': 'Pricing',
    'de': 'Preise',
    'pt-BR': 'Preços',
    'ms-MY': 'Harga',
    'es': 'Precios',
    'fr': 'Tarifs',
    'ja': '料金',
    'id': 'Harga',
  },
  'search': {
    'en': 'Search',
    'de': 'Suche',
    'pt-BR': 'Pesquisa',
    'ms-MY': 'Carian',
    'es': 'Buscar',
    'fr': 'Recherche',
    'ja': '検索',
    'id': 'Cari',
  },
  'privacy': {
    'en': 'Privacy Policy',
    'de': 'Datenschutz',
    'pt-BR': 'Privacidade',
    'ms-MY': 'Privasi',
    'es': 'Privacidad',
    'fr': 'Politique de confidentialité',
    'ja': 'プライバシーポリシー',
    'id': 'Kebijakan Privasi',
  },
  'terms': {
    'en': 'Terms of Service',
    'de': 'AGB',
    'pt-BR': 'Termos',
    'ms-MY': 'Terma',
    'es': 'Términos',
    'fr': 'Conditions d\'utilisation',
    'ja': '利用規約',
    'id': 'Syarat Layanan',
  },
  'imprint': {
    'en': 'Imprint',
    'de': 'Impressum',
    'pt-BR': 'Impressão',
    'ms-MY': 'Cetakan',
    'es': 'Aviso Legal',
    'fr': 'Mentions légales',
    'ja': '会社概要',
    'id': 'Imprint',
  },
  'faq': {
    'en': 'FAQ',
    'de': 'FAQ',
    'pt-BR': 'FAQ',
    'ms-MY': 'FAQ',
    'es': 'FAQ',
    'fr': 'FAQ',
    'ja': 'よくある質問',
    'id': 'FAQ',
  },
};

/**
 * Get localized breadcrumb label for a key
 */
function getLabel(key: string, locale: Locale): string {
  return BREADCRUMB_LABELS[key]?.[locale] || BREADCRUMB_LABELS[key]?.['en'] || key;
}

/**
 * Get blog post title from slug (sync version for SSR)
 * This is a simplified version - in production, you'd load this from blog metadata
 */
function getBlogPostTitle(slug: string, locale: Locale): string {
  // Try to load the blog post title
  try {
    const { getPostBySlug } = require('../blog/blogPosts');
    const post = getPostBySlug(slug, locale);
    return post?.title || slug;
  } catch (e) {
    return slug;
  }
}

/**
 * Get knowledge article title from slug (sync version for SSR)
 */
function getKnowledgeArticleTitle(slug: string, locale: Locale): string {
  try {
    const { getArticleBySlug } = require('../knowledge/knowledgeArticles');
    const article = getArticleBySlug(slug, locale);
    return article?.title || slug;
  } catch (e) {
    return slug;
  }
}

/**
 * Get category label from slug (handles both English and localized slugs)
 */
function getCategoryLabel(slug: string, locale: Locale): string {
  try {
    const { getCategoryBySlug, getCategoryByLocalizedSlug } = require('../blog/categories');

    // Try localized slug first, then English slug
    let category = getCategoryByLocalizedSlug(slug, locale);
    if (!category) {
      category = getCategoryBySlug(slug);
    }

    if (category) {
      const localeKey = locale === 'pt-BR' ? 'br' : locale === 'ms-MY' ? 'my' : locale;
      return category.label[localeKey as keyof typeof category.label] || category.label.en;
    }
  } catch (e) {
    // Fallback
  }
  return slug;
}

/**
 * Get tag display name from slug
 */
function getTagLabel(slug: string): string {
  try {
    const { toTagDisplay } = require('../blog/categories');
    return toTagDisplay(slug);
  } catch (e) {
    return slug;
  }
}

/**
 * Generate BreadcrumbList schema for a given pathname
 *
 * @param pathname - Current URL pathname (with locale prefix)
 * @param locale - Current locale
 * @returns BreadcrumbList schema or null if homepage or invalid
 */
export function generateBreadcrumbList(
  pathname: string,
  locale: Locale
): BreadcrumbListSchema | null {
  const BASE_URL = 'https://gpt-watermark-remover.com';

  // Remove trailing slash for consistency (except root)
  const cleanPath = pathname === '/' ? '/' : pathname.replace(/\/$/, '');

  // Homepage should not have breadcrumbs
  if (cleanPath === '/' || cleanPath === '/en' || cleanPath === '/de' || cleanPath === '/pt-br' || cleanPath === '/my' || cleanPath === '/es' || cleanPath === '/fr' || cleanPath === '/ja' || cleanPath === '/id') {
    return null;
  }

  // Remove locale prefix for path analysis
  const pathWithoutLocale = cleanPath.replace(/^\/(en|de|pt-br|my|es|fr|ja|id)(\/|$)/, '$2') || '/';

  // Build breadcrumb items
  const items: BreadcrumbItem[] = [];

  // Always start with Home - use correct locale prefixes
  const homeUrl = locale === 'en'
    ? `${BASE_URL}/`
    : locale === 'de'
    ? `${BASE_URL}/de`
    : locale === 'pt-BR'
    ? `${BASE_URL}/pt-br`
    : locale === 'ms-MY'
    ? `${BASE_URL}/my`
    : locale === 'es'
    ? `${BASE_URL}/es`
    : locale === 'fr'
    ? `${BASE_URL}/fr`
    : locale === 'ja'
    ? `${BASE_URL}/ja`
    : locale === 'id'
    ? `${BASE_URL}/id`
    : `${BASE_URL}/`;

  items.push({
    '@type': 'ListItem',
    position: 1,
    name: getLabel('home', locale),
    item: homeUrl,
  });

  // Parse the path segments
  const segments = pathWithoutLocale.split('/').filter(Boolean);

  if (segments.length === 0) {
    return null; // This shouldn't happen, but safety check
  }

  // Handle different page types
  const firstSegment = segments[0];

  // Blog routes
  if (firstSegment === 'blog') {
    // Add "Blog" breadcrumb
    const localePrefix = locale === 'en' ? '' : locale === 'pt-BR' ? '/pt-br' : locale === 'ms-MY' ? '/my' : `/${locale}`;
    const blogPath = `${localePrefix}/blog`;
    items.push({
      '@type': 'ListItem',
      position: 2,
      name: getLabel('blog', locale),
      item: `${BASE_URL}${blogPath}`,
    });

    // Blog post: /blog/:slug
    if (segments.length === 2 && segments[1] !== 'category' && segments[1] !== 'tag') {
      const postSlug = segments[1];
      const postTitle = getBlogPostTitle(postSlug, locale);
      items.push({
        '@type': 'ListItem',
        position: 3,
        name: postTitle,
        item: getCanonicalUrl(cleanPath),
      });
    }

    // Blog category: /blog/category/:slug OR /blog/kategorie/:slug, /blog/categoria/:slug, /blog/kategori/:slug
    // Spanish uses 'categoria' as well
    else if (segments.length === 3 && (segments[1] === 'category' || segments[1] === 'kategorie' || segments[1] === 'categoria' || segments[1] === 'kategori')) {
      const categorySlug = segments[2];
      const categoryLabel = getCategoryLabel(categorySlug, locale);
      items.push({
        '@type': 'ListItem',
        position: 3,
        name: categoryLabel,
        item: getCanonicalUrl(cleanPath),
      });
    }

    // Blog tag: /blog/tag/:slug or /blog/etiqueta/:slug (Spanish)
    else if (segments.length === 3 && (segments[1] === 'tag' || segments[1] === 'etiqueta')) {
      const tagSlug = segments[2];
      const tagLabel = getTagLabel(tagSlug);
      items.push({
        '@type': 'ListItem',
        position: 3,
        name: tagLabel,
        item: getCanonicalUrl(cleanPath),
      });
    }
  }

  // Knowledge routes (including Spanish 'conocimiento', French 'connaissances')
  else if (firstSegment === 'knowledge' || firstSegment === 'wissen' || firstSegment === 'conhecimento' || firstSegment === 'pengetahuan' || firstSegment === 'conocimiento' || firstSegment === 'connaissances') {
    // Add "Knowledge" breadcrumb
    const knowledgePath = locale === 'en'
      ? '/knowledge'
      : locale === 'de'
      ? '/de/wissen'
      : locale === 'pt-BR'
      ? '/pt-br/conhecimento'
      : locale === 'es'
      ? '/es/conocimiento'
      : locale === 'fr'
      ? '/fr/connaissances'
      : locale === 'ja'
      ? '/ja/knowledge'
      : locale === 'id'
      ? '/id/pengetahuan'
      : '/my/pengetahuan';

    items.push({
      '@type': 'ListItem',
      position: 2,
      name: getLabel('knowledge', locale),
      item: `${BASE_URL}${knowledgePath}`,
    });

    // Knowledge article: /knowledge/:slug
    if (segments.length === 2) {
      const articleSlug = segments[1];
      const articleTitle = getKnowledgeArticleTitle(articleSlug, locale);
      items.push({
        '@type': 'ListItem',
        position: 3,
        name: articleTitle,
        item: getCanonicalUrl(cleanPath),
      });
    }
  }

  // pSEO pages (single level, direct from home)
  // Includes ChatGPT, Gemini, Claude, Student pages for all locales
  else if (
    // ChatGPT pages
    pathWithoutLocale === '/remove-chatgpt-watermarks' ||
    pathWithoutLocale === '/chatgpt-wasserzeichen-entfernen' ||
    pathWithoutLocale === '/remover-marca-dagua-chatgpt' ||
    pathWithoutLocale === '/buang-tanda-air-chatgpt' ||
    pathWithoutLocale === '/eliminar-marca-de-agua-chatgpt' ||
    pathWithoutLocale === '/supprimer-filigrane-chatgpt' ||
    pathWithoutLocale === '/chatgpt-watermark-remover' ||
    pathWithoutLocale === '/hapus-watermark-chatgpt' ||
    // Gemini pages
    pathWithoutLocale === '/remove-gemini-watermarks' ||
    pathWithoutLocale === '/gemini-wasserzeichen-entfernen' ||
    pathWithoutLocale === '/remover-marca-dagua-gemini' ||
    pathWithoutLocale === '/buang-tanda-air-gemini' ||
    pathWithoutLocale === '/eliminar-marca-de-agua-gemini' ||
    pathWithoutLocale === '/supprimer-filigrane-gemini' ||
    pathWithoutLocale === '/gemini-watermark-remover' ||
    pathWithoutLocale === '/hapus-watermark-gemini' ||
    // Claude pages
    pathWithoutLocale === '/remove-claude-watermarks' ||
    pathWithoutLocale === '/claude-wasserzeichen-entfernen' ||
    pathWithoutLocale === '/remover-marca-dagua-claude' ||
    pathWithoutLocale === '/buang-tanda-air-claude' ||
    pathWithoutLocale === '/eliminar-marca-de-agua-claude' ||
    pathWithoutLocale === '/supprimer-filigrane-claude' ||
    pathWithoutLocale === '/claude-watermark-remover' ||
    pathWithoutLocale === '/hapus-watermark-claude' ||
    // Student pages
    pathWithoutLocale === '/ai-watermark-remover-students' ||
    pathWithoutLocale === '/ki-wasserzeichen-entferner-studenten' ||
    pathWithoutLocale === '/removedor-ai-estudantes' ||
    pathWithoutLocale === '/penyingkir-tanda-air-ai-pelajar' ||
    pathWithoutLocale === '/eliminar-marca-de-agua-texto-estudiantes' ||
    pathWithoutLocale === '/supprimeur-filigrane-ia-etudiants' ||
    pathWithoutLocale === '/penghapus-watermark-ai-mahasiswa' ||
    // Free ChatGPT pages
    pathWithoutLocale === '/free-chatgpt-watermark-remover' ||
    pathWithoutLocale === '/kostenloser-chatgpt-wasserzeichen-entferner' ||
    pathWithoutLocale === '/removedor-marca-dagua-chatgpt-gratis' ||
    pathWithoutLocale === '/penyingkir-tanda-air-chatgpt-percuma' ||
    pathWithoutLocale === '/eliminador-marca-agua-chatgpt-gratis' ||
    pathWithoutLocale === '/supprimeur-filigrane-chatgpt-gratuit' ||
    pathWithoutLocale === '/penghapus-watermark-chatgpt-gratis' ||
    // Free AI Text pages
    pathWithoutLocale === '/free-ai-text-watermark-remover' ||
    pathWithoutLocale === '/kostenloser-ki-text-wasserzeichen-entferner' ||
    pathWithoutLocale === '/removedor-marca-dagua-texto-ia-gratis' ||
    pathWithoutLocale === '/penyingkir-tanda-air-teks-ai-percuma' ||
    pathWithoutLocale === '/eliminador-marca-agua-texto-ia-gratis' ||
    pathWithoutLocale === '/supprimeur-filigrane-texte-ia-gratuit' ||
    pathWithoutLocale === '/penghapus-watermark-teks-ai-gratis' ||
    // Free AI pages
    pathWithoutLocale === '/free-ai-watermark-remover' ||
    pathWithoutLocale === '/kostenloser-ki-wasserzeichen-entferner' ||
    pathWithoutLocale === '/removedor-marca-dagua-ia-gratis' ||
    pathWithoutLocale === '/penyingkir-tanda-air-ai-percuma' ||
    pathWithoutLocale === '/eliminador-marca-agua-ia-gratis' ||
    pathWithoutLocale === '/supprimeur-filigrane-ia-gratuit' ||
    pathWithoutLocale === '/penghapus-watermark-ai-gratis' ||
    // Free GPT pages
    pathWithoutLocale === '/free-gpt-watermark-remover' ||
    pathWithoutLocale === '/kostenloser-gpt-wasserzeichen-entferner' ||
    pathWithoutLocale === '/removedor-marca-dagua-gpt-gratis' ||
    pathWithoutLocale === '/penyingkir-tanda-air-gpt-percuma' ||
    pathWithoutLocale === '/eliminador-marca-agua-gpt-gratis' ||
    pathWithoutLocale === '/supprimeur-filigrane-gpt-gratuit' ||
    pathWithoutLocale === '/penghapus-watermark-gpt-gratis'
  ) {
    // Get the page title from SEOHead config or urlMappings
    // These pages are direct children of home, so just add them
    // Find the canonical English path first
    let canonicalPath = pathWithoutLocale;
    for (const [canonical, translations] of Object.entries(urlMappings)) {
      for (const translation of Object.values(translations)) {
        if (translation === pathWithoutLocale) {
          canonicalPath = canonical;
          break;
        }
      }
    }

    // Get page title (simplified - you'd want to import from SEOHead config)
    const pageTitles: Record<string, Record<Locale, string>> = {
      '/remove-chatgpt-watermarks': {
        'en': 'Remove ChatGPT Watermarks',
        'de': 'ChatGPT Wasserzeichen entfernen',
        'pt-BR': 'Remover Marcas d\'água ChatGPT',
        'ms-MY': 'Buang Tanda Air ChatGPT',
        'es': 'Eliminar Marca de Agua ChatGPT',
        'fr': 'Supprimer les Filigranes ChatGPT',
        'ja': 'ChatGPT透かしを削除',
        'id': 'Hapus Watermark ChatGPT',
      },
      '/remove-gemini-watermarks': {
        'en': 'Remove Gemini Watermarks',
        'de': 'Gemini Wasserzeichen entfernen',
        'pt-BR': 'Remover Marcas d\'água Gemini',
        'ms-MY': 'Buang Tanda Air Gemini',
        'es': 'Eliminar Marca de Agua Gemini',
        'fr': 'Supprimer les Filigranes Gemini',
        'ja': 'Gemini透かしを削除',
        'id': 'Hapus Watermark Gemini',
      },
      '/remove-claude-watermarks': {
        'en': 'Remove Claude Watermarks',
        'de': 'Claude Wasserzeichen entfernen',
        'pt-BR': 'Remover Marcas d\'água Claude',
        'ms-MY': 'Buang Tanda Air Claude',
        'es': 'Eliminar Marca de Agua Claude',
        'fr': 'Supprimer les Filigranes Claude',
        'ja': 'Claude透かしを削除',
        'id': 'Hapus Watermark Claude',
      },
      '/ai-watermark-remover-students': {
        'en': 'AI Watermark Remover for Students',
        'de': 'KI-Wasserzeichen-Entferner für Studenten',
        'pt-BR': 'Removedor de Marca d\'água de IA para Estudantes',
        'ms-MY': 'Penyingkir Tanda Air AI untuk Pelajar',
        'es': 'Eliminador de Marca de Agua de IA para Estudiantes',
        'fr': 'Suppresseur de Filigrane IA pour Étudiants',
        'ja': '学生向けAI透かし削除ツール',
        'id': 'Penghapus Watermark AI untuk Pelajar',
      },
      '/free-chatgpt-watermark-remover': {
        'en': 'Free ChatGPT Watermark Remover',
        'de': 'Kostenloser ChatGPT Wasserzeichen-Entferner',
        'pt-BR': 'Removedor de Marca d\'água ChatGPT Grátis',
        'ms-MY': 'Penyingkir Tanda Air ChatGPT Percuma',
        'es': 'Eliminador de Marca de Agua ChatGPT Gratis',
        'fr': 'Suppresseur de Filigrane ChatGPT Gratuit',
        'ja': '無料ChatGPT透かし削除ツール',
        'id': 'Penghapus Watermark ChatGPT Gratis',
      },
      '/free-ai-text-watermark-remover': {
        'en': 'Free AI Text Watermark Remover',
        'de': 'Kostenloser KI-Text Wasserzeichen-Entferner',
        'pt-BR': 'Removedor de Marca d\'água de Texto IA Grátis',
        'ms-MY': 'Penyingkir Tanda Air Teks AI Percuma',
        'es': 'Eliminador de Marca de Agua de Texto IA Gratis',
        'fr': 'Suppresseur de Filigrane de Texte IA Gratuit',
        'ja': '無料AIテキスト透かし削除ツール',
        'id': 'Penghapus Watermark Teks AI Gratis',
      },
      '/free-ai-watermark-remover': {
        'en': 'Free AI Watermark Remover',
        'de': 'Kostenloser KI Wasserzeichen-Entferner',
        'pt-BR': 'Removedor de Marca d\'água de IA Grátis',
        'ms-MY': 'Penyingkir Tanda Air AI Percuma',
        'es': 'Eliminador de Marca de Agua de IA Gratis',
        'fr': 'Suppresseur de Filigrane IA Gratuit',
        'ja': '無料AI透かし削除ツール',
        'id': 'Penghapus Watermark AI Gratis',
      },
      '/free-gpt-watermark-remover': {
        'en': 'Free GPT Watermark Remover',
        'de': 'Kostenloser GPT Wasserzeichen-Entferner',
        'pt-BR': 'Removedor de Marca d\'água GPT Grátis',
        'ms-MY': 'Penyingkir Tanda Air GPT Percuma',
        'es': 'Eliminador de Marca de Agua GPT Gratis',
        'fr': 'Suppresseur de Filigrane GPT Gratuit',
        'ja': '無料GPT透かし削除ツール',
        'id': 'Penghapus Watermark GPT Gratis',
      },
    };

    const pageTitle = pageTitles[canonicalPath]?.[locale] || firstSegment;

    items.push({
      '@type': 'ListItem',
      position: 2,
      name: pageTitle,
      item: getCanonicalUrl(cleanPath),
    });
  }

  // Other mapped pages (About, Detect, Document, etc.)
  else if (urlMappings[`/${firstSegment}`] || Object.values(urlMappings).some(m => Object.values(m).includes(`/${firstSegment}`))) {
    // Find canonical path
    let canonicalPath = `/${firstSegment}`;
    for (const [canonical, translations] of Object.entries(urlMappings)) {
      for (const translation of Object.values(translations)) {
        if (translation === pathWithoutLocale) {
          canonicalPath = canonical;
          break;
        }
      }
    }

    // Get the label
    const baseKey = canonicalPath.replace('/', '');
    const label = getLabel(baseKey, locale);

    items.push({
      '@type': 'ListItem',
      position: 2,
      name: label,
      item: getCanonicalUrl(cleanPath),
    });
  }

  // Only return breadcrumbs if we have more than just "Home"
  if (items.length <= 1) {
    return null;
  }

  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items,
  };
}

/**
 * Convert BreadcrumbList schema to JSON-LD script tag content
 */
export function breadcrumbsToJsonLd(schema: BreadcrumbListSchema | null): string {
  if (!schema) return '';
  return JSON.stringify(schema, null, 0); // Minified for production
}

/**
 * Generate breadcrumb script tag for SSR injection
 * Includes data-breadcrumb attribute for client-side management
 */
export function generateBreadcrumbScript(pathname: string, locale: Locale): string {
  const schema = generateBreadcrumbList(pathname, locale);
  if (!schema) return '';

  const jsonLd = breadcrumbsToJsonLd(schema);
  return `<script type="application/ld+json" data-breadcrumb="true">${jsonLd}</script>`;
}
